// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormNumberTextField } from '@/components/form-number-text-field';
import { FormTextField } from '@/components/form-text-field';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { ChargeFormData } from '@/project/types/charge-form-schema';
import { Box, Button, Typography } from '@mui/material';
import { Control, useFormState, useWatch } from 'react-hook-form';

const compactInputSx = { fontSize: '0.8125rem' } as const;
const compactSx = { '& .MuiInputBase-input': compactInputSx };

// Hides the default TextField helperText slot so the pricing row height
// stays constant whether or not an error is present. Errors render in a
// dedicated row below, column-aligned with the offending field.
const hideHelperTextSx = {
  '& .MuiFormHelperText-root': { display: 'none' },
};

interface Props {
  control: Control<ChargeFormData>;
  pricingMode: string;
  currencyCode: IsoCurrencyCode;
  saveLabel: string;
  onSave: () => void;
  onCancel: () => void;
}

export function ChargeFormFields(props: Props) {
  const quantity = useWatch({ control: props.control, name: 'quantity' });
  const unitPrice = useWatch({ control: props.control, name: 'unitPrice' });
  const { errors } = useFormState({
    control: props.control,
    name: ['quantity', 'unitPrice'],
  });

  const qty = parseInt(quantity ?? '', 10) || 0;
  const price =
    parseCurrencyAmount((unitPrice?.amount ?? '').replace(/,/g, '')) ??
    new CurrencyDecimal(0);
  const computedTotal =
    props.pricingMode === 'PER_UNIT' ? price.times(qty) : price;
  // Both inputs need a value before the running total is meaningful; hide
  // the "= $X.XX" readout until the user has entered enough to compute it.
  const canComputeTotal = qty > 0 && price.greaterThan(0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Row 1: Label + Bill date */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <FormTextField
          name="label"
          control={props.control}
          label="Label"
          autoFocus
          sx={compactSx}
        />
        <FormDatePicker
          name="expectedBillableDate"
          control={props.control}
          label="Expected bill date"
          isClearable
        />
      </Box>

      {/* Row 2: Service period start + end */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <FormDatePicker
          name="servicePeriodStart"
          control={props.control}
          label="Service period start"
          isClearable
        />
        <FormDatePicker
          name="servicePeriodEnd"
          control={props.control}
          label="Service period end"
          isClearable
        />
      </Box>

      {/* Row 3: Pricing inputs + actions. Helper text is hidden on the
          fields themselves; the row below is a column-aligned error row
          so a validation error does not grow this row's height. */}
      {props.pricingMode === 'PER_UNIT' ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto auto auto auto 1fr auto auto',
            alignItems: 'center',
            columnGap: 1.5,
            rowGap: 0.25,
          }}
        >
          {/* Field row */}
          <Box sx={hideHelperTextSx}>
            <FormNumberTextField
              name="quantity"
              control={props.control}
              label="Quantity"
              InputProps={{ sx: compactInputSx }}
            />
          </Box>
          <Box
            component="span"
            sx={{ fontSize: '0.625rem', color: 'text.secondary' }}
          >
            ×
          </Box>
          <Box sx={hideHelperTextSx}>
            <FormCurrencyTextField
              name="unitPrice"
              control={props.control}
              label="Unit price"
              lockTo={props.currencyCode}
            />
          </Box>
          <Box
            component="span"
            sx={{ fontSize: '0.625rem', color: 'text.secondary' }}
          >
            {canComputeTotal
              ? `= ${formatCurrencyValue({ amount: computedTotal, isoCode: props.currencyCode })}`
              : ''}
          </Box>
          <Box />
          <Button onClick={props.onCancel} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={props.onSave}
            sx={{ textTransform: 'none' }}
          >
            {props.saveLabel}
          </Button>

          {/* Helper row: errors under quantity and unit price, blanks
              under every non-field column. */}
          <Typography variant="caption" color="error">
            {errors.quantity?.message ?? ''}
          </Typography>
          <Box />
          <Typography variant="caption" color="error">
            {errors.unitPrice?.amount?.message ?? ''}
          </Typography>
          <Box />
          <Box />
          <Box />
          <Box />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto auto',
            alignItems: 'center',
            columnGap: 1.5,
            rowGap: 0.25,
          }}
        >
          {/* Field row */}
          <Box sx={hideHelperTextSx}>
            <FormCurrencyTextField
              name="unitPrice"
              control={props.control}
              label="Amount"
              lockTo={props.currencyCode}
            />
          </Box>
          <Box />
          <Button onClick={props.onCancel} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={props.onSave}
            sx={{ textTransform: 'none' }}
          >
            {props.saveLabel}
          </Button>

          {/* Helper row: error under Amount, blanks under spacer/buttons. */}
          <Typography variant="caption" color="error">
            {errors.unitPrice?.amount?.message ?? ''}
          </Typography>
          <Box />
          <Box />
          <Box />
        </Box>
      )}
    </Box>
  );
}
